import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { SalesLayout } from '../components/crm/SalesLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Briefcase, Plus, Search, MapPin } from 'lucide-react';
import { crmFetch } from '../components/crm/crmApi';
import { JobModal } from '../components/crm/JobModal';
import { JOB_STATUSES, jobStatusMeta, salaryRange, feeLabel, fmtDate } from '../components/crm/constants';

const STATUS_FILTERS = [{ key: 'all', label: 'All' }, ...JOB_STATUSES.map((s) => ({ key: s.value, label: s.label }))];

export default function SalesJobs() {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const isAdmin = userProfile?.role === 'jobasti_admin';
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableMissing, setTableMissing] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    const res = await crmFetch('/jobs');
    if (res.tableMissing) setTableMissing(true);
    else if (res.success) { setJobs(res.data || []); setTableMissing(false); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const q = search.trim().toLowerCase();
  const filtered = jobs.filter((j) => {
    if (statusFilter !== 'all' && j.status !== statusFilter) return false;
    if (!q) return true;
    return [j.title, j.accountName, j.location].some((v) => (v || '').toLowerCase().includes(q));
  });

  return (
    <SalesLayout>
      <div data-testid="jobs-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Jobs</h1>
            <p className="text-sm text-gray-500 mt-1">Recruitment openings across your accounts.</p>
          </div>
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setModalOpen(true)} data-testid="new-job-btn">
            <Plus className="w-4 h-4 mr-2" /> New Job
          </Button>
        </div>

        {tableMissing && (
          <Card className="border-amber-300 bg-amber-50" data-testid="crm-migration-notice">
            <CardContent className="p-6 text-sm text-amber-800">
              Recruitment tables not migrated yet — run <code className="px-1 bg-amber-100 rounded">2026-06-15-crm-phase2.sql</code> in the Supabase SQL Editor.
            </CardContent>
          </Card>
        )}

        {!tableMissing && (
          <>
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input className="pl-9" placeholder="Search jobs…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="jobs-search-input" />
              </div>
              <div className="flex gap-1 flex-wrap">
                {STATUS_FILTERS.map((f) => (
                  <button
                    key={f.key}
                    onClick={() => setStatusFilter(f.key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      statusFilter === f.key ? 'bg-blue-600 text-white' : 'bg-white border text-gray-600 hover:bg-gray-100'
                    }`}
                    data-testid={`jobs-filter-${f.key}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50/60 text-left text-xs text-gray-500 uppercase tracking-wide">
                        <th className="px-4 py-3 font-medium">Job</th>
                        <th className="px-4 py-3 font-medium">Status</th>
                        <th className="px-4 py-3 font-medium text-center">Pipeline</th>
                        <th className="px-4 py-3 font-medium">Salary</th>
                        <th className="px-4 py-3 font-medium text-center">Fee</th>
                        <th className="px-4 py-3 font-medium">Target</th>
                        {isAdmin && <th className="px-4 py-3 font-medium">Owner</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {loading && <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400">Loading…</td></tr>}
                      {!loading && filtered.length === 0 && (
                        <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400">
                          {jobs.length === 0 ? 'No jobs yet — create your first opening.' : 'No jobs match your filters.'}
                        </td></tr>
                      )}
                      {filtered.map((j) => {
                        const sm = jobStatusMeta(j.status);
                        const sal = salaryRange(j.salary_min, j.salary_max, j.currency);
                        return (
                          <tr
                            key={j.id}
                            onClick={() => navigate(`/crm-jobs/${j.id}`)}
                            className="border-b last:border-0 hover:bg-blue-50/40 cursor-pointer transition-colors"
                            data-testid={`job-row-${j.id}`}
                          >
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center shrink-0">
                                  <Briefcase className="w-4 h-4" />
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900">{j.title}</p>
                                  <p className="text-xs text-gray-500 flex items-center gap-1.5">
                                    {j.accountName || '—'}
                                    {j.location && <><span className="text-gray-300">·</span><MapPin className="w-3 h-3" />{j.location}</>}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3"><Badge className={`${sm.className} border-0`}>{sm.label}</Badge></td>
                            <td className="px-4 py-3 text-center">
                              <span className="text-gray-900 font-medium">{j.activeCandidateCount}</span>
                              <span className="text-gray-400"> / {j.candidateCount}</span>
                              {j.placedCount > 0 && <span className="text-emerald-600 text-xs ml-1">· {j.placedCount} placed</span>}
                            </td>
                            <td className="px-4 py-3 text-gray-600">{sal || '—'}</td>
                            <td className="px-4 py-3 text-center text-gray-600">{feeLabel(j) || '—'}</td>
                            <td className="px-4 py-3 text-gray-600">{j.target_date ? fmtDate(j.target_date) : '—'}</td>
                            {isAdmin && <td className="px-4 py-3 text-gray-600">{j.ownerName || '—'}</td>}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      <JobModal open={modalOpen} onOpenChange={setModalOpen} onSaved={load} />
    </SalesLayout>
  );
}
